/**
 * Saved-draft hydration
 *
 * Restores "is the draft on screen already published?" when a build session is
 * resumed rather than saved in this tab.
 *
 * `initializeTemplateBuild` clears `savedDraftSignature`, so after a reload the
 * Save button would offer to publish a draft that is already published — and a
 * publish writes a revision unconditionally, which is the duplicate-revision
 * churn this whole change exists to stop (CUS-1169).
 *
 * The status payload cannot answer this on its own. `created_template_id` only
 * says the task published at some point; it says nothing about whether the
 * draft has been edited since. Marking the draft saved on that basis would tell
 * a provider their unsaved chat edit was already published and then disable the
 * button they need — trading a junk revision for silent data loss, which is a
 * worse version of the same lie.
 *
 * So we ask the server what it actually published and compare content.
 */

import { useEffect } from 'react';
import { useGetTemplateByIdQuery } from '@features/templates/store/api';
import { useAppDispatch, useAppSelector } from '@store/hooks';

import { setSavedDraftSignature } from '../store/chatSlice';
import { selectCreatedTemplateId, selectSavedDraftSignature } from '../store/selectors';
import { draftTemplateSignature } from '../types';

interface UseHydrateSavedDraftSignatureResult {
  /** True while the published revision is being fetched. */
  isReconciling: boolean;
}

/**
 * @param enabled - Pass false where the provider templates endpoint cannot
 *   answer for this template (admin mode edits on behalf of another user).
 *   Hydration is then skipped and Save stays available.
 */
export const useHydrateSavedDraftSignature = (
  enabled: boolean,
): UseHydrateSavedDraftSignatureResult => {
  const dispatch = useAppDispatch();
  const createdTemplateId = useAppSelector(selectCreatedTemplateId);
  const savedDraftSignature = useAppSelector(selectSavedDraftSignature);

  // Only when this session has a published template but no fingerprint for it —
  // i.e. the save happened before this page load.
  const needsHydration = enabled && Boolean(createdTemplateId) && savedDraftSignature === null;

  const { data, isFetching } = useGetTemplateByIdQuery(
    { templateId: createdTemplateId ?? '' },
    { skip: !needsHydration },
  );

  useEffect(() => {
    if (!needsHydration || !data) return;

    dispatch(setSavedDraftSignature(draftTemplateSignature(data.revision)));
  }, [needsHydration, data, dispatch]);

  return { isReconciling: needsHydration && isFetching };
};
